import {skipToken} from '@reduxjs/toolkit/query';
import {DataStatus, showNotification} from 'platform/components';

import {useRef} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {GetFileByIdApiResponse, useGetFileByIdQuery} from '@dms/api/base';
import {useGetAdditionalNoteQuery, useSetAdditionalNoteMutation} from '@dms/api/sales';
import {
  GetVehicleApiResponse,
  useGetVehicleQuery,
  useUpdateVehicleMutation,
} from '@dms/api/vehicle';
import {useGetServiceVehicleV2Query, usePatchServiceVehicleMutation} from '@dms/api/workshop';
import {
  ServiceVehicle,
  ServiceVehicleValidationSchema,
  toAPIVehicle,
  toBaseVehicle,
  useGetCarFeatures,
  useUpdateVehicleType,
  useVehicleDocumentSync,
  VehicleForm as VFKit,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {FullScreenModal, handleApiError, is404ApiError} from '@dms/shared';

import {Nullish} from 'shared/web';

import {ServiceVehicleForm} from './ServiceVehicleForm';

interface EditServiceVehicleModalProps {
  vehicleId: string;
  onSaved: (vehicleId: string) => void;
  onDiscard: () => void;
  isEmbedded?: boolean;
}

interface EditServiceVehicleFormProps extends EditServiceVehicleModalProps {
  vehicle: GetVehicleApiResponse;
  certificateFile: GetFileByIdApiResponse | undefined;
  additionalNote: string | Nullish;
  serviceVehicleId: string | Nullish;
  vehicleClassId: string | Nullish;
}

// Split from the modal so every query has settled first: react-hook-form captures `defaultValues`
// at mount, so seeding them from a still-loading query would permanently leave the form empty.
function EditServiceVehicleForm(props: EditServiceVehicleFormProps) {
  const [updateVehicleMutation, {isLoading: isUpdating}] = useUpdateVehicleMutation();
  const [patchServiceVehicleMutation] = usePatchServiceVehicleMutation();
  const [setAdditionalNoteMutation] = useSetAdditionalNoteMutation();
  const [updateVehicleType] = useUpdateVehicleType(props.vehicleId, props.vehicle.type);
  const {syncDocuments} = useVehicleDocumentSync();
  const [getCarFeatures] = useGetCarFeatures();

  // isUpdating only covers the base mutation, leaving the button live across the car-features await.
  const isSubmitting = useRef(false);
  const isDiscarded = useRef(false);

  const handleDiscard = () => {
    isDiscarded.current = true;
    props.onDiscard();
  };

  const handleSave = async (values: ServiceVehicle) => {
    if (isSubmitting.current) {
      return;
    }

    // Vehicle type chips are deselectable, and toAPIVehicle turns an empty selection into
    // VEHICLETYPE_PASSENGER_CAR - which would silently convert the vehicle. Zod does not catch it
    // either, since the field is an array and `[]` is valid.
    if (isNilOrEmpty(values.type)) {
      showNotification.warning(i18n.t('errors.invalid_submit'));
      return;
    }

    isSubmitting.current = true;

    try {
      const sourcingData = await getCarFeatures(values);
      const apiPayload = toAPIVehicle(
        values,
        props.vehicle.features ?? null,
        sourcingData,
        props.vehicle
      );

      // Mirrors EditBaseVehicle's service-vehicle save path, minus its sale-only steps. Unlike that
      // one, every step here aborts the chain on failure rather than swallowing its own error, so a
      // partial save never reports success.
      const onUpdate = () =>
        updateVehicleMutation({
          vehicleId: props.vehicleId,
          updateVehicleRequestBody: apiPayload,
          // SERVICE_ON_CREATION is the only stage defined, and a stored vehicle may predate it.
          validateMandatoryFields: false,
        })
          .unwrap()
          .then(() => {
            const nextVehicleClassId = values.vehicleClassId ?? null;
            const originalVehicleClassId = props.vehicleClassId ?? null;

            // Both sides need the ?? null: `undefined !== null` would fire a PATCH on every save
            // and wipe the stored class.
            if (isNotNil(props.serviceVehicleId) && nextVehicleClassId !== originalVehicleClassId) {
              return patchServiceVehicleMutation({
                serviceVehicleId: props.serviceVehicleId,
                patchServiceVehicleRequestBody: {vehicleClassId: nextVehicleClassId},
              })
                .unwrap()
                .then(() => undefined);
            }

            return Promise.resolve();
          })
          .then(() => {
            // The note is not a vehicle property - it has its own endpoint, so toAPIVehicle omits it.
            if ((values.additionalNote ?? '') === (props.additionalNote ?? '')) {
              return Promise.resolve();
            }

            return setAdditionalNoteMutation({
              vehicleId: props.vehicleId,
              additionalNoteRequestBody: {note: values.additionalNote ?? ''},
            })
              .unwrap()
              .then(() => undefined);
          })
          .then(() =>
            syncDocuments({
              vehicleId: props.vehicleId,
              contextTarget: 'vehicle',
              fileFields: [
                {
                  fieldValue: values.batteryCertificateDocument,
                  documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
                },
              ],
            })
          )
          .then((documentResults) => {
            // syncDocuments catches per file and resolves, so a failure only shows up in its
            // results. The vehicle is already written, but the certificate is not attached - keep
            // the form open rather than reporting a save that half happened.
            if (documentResults.some((result) => !result.success)) {
              return;
            }

            // The save outlives the form: Discard stays live across the getCarFeatures await, and
            // calling back after that would assign a vehicle the user had already dismissed.
            if (isDiscarded.current) {
              return;
            }

            props.onSaved(props.vehicleId);
          })
          .catch(handleApiError);

      // updateVehicleType resolves {success: false} instead of rejecting, so without this branch a
      // rejected conversion would still write the new type's payload and close as a success.
      const typeChangeResult = await updateVehicleType(apiPayload.type);

      if (!typeChangeResult.success) {
        return;
      }

      await onUpdate();
    } finally {
      isSubmitting.current = false;
    }
  };

  const defaultValues = toBaseVehicle(props.vehicle, props.certificateFile);

  return (
    <VFKit.Form
      mode="onBlur"
      experimentalZodSchema={ServiceVehicleValidationSchema}
      defaultValues={{
        ...defaultValues,
        vehicleClassId: props.vehicleClassId,
        additionalNote: props.additionalNote,
      }}
    >
      <ServiceVehicleForm
        onSave={handleSave}
        onDiscard={handleDiscard}
        isCreating={isUpdating}
        vehicleId={props.vehicleId}
        isEmbedded={props.isEmbedded}
        // Without a service vehicle the class cannot be persisted, so the field must not be offered.
        shouldShowVehicleClass={isNotNil(props.serviceVehicleId)}
        modalKeys={{
          headline: i18n.t('entity.vehicle.labels.editServiceVehicle'),
          create: i18n.t('general.actions.save'),
        }}
      />
    </VFKit.Form>
  );
}

export function EditServiceVehicleModal(props: EditServiceVehicleModalProps) {
  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery({vehicleId: props.vehicleId}, {skip: isNilOrEmpty(props.vehicleId)});

  const certificateFileId = vehicle?.certificateFileId;
  const {
    data: certificateFile,
    isLoading: isCertificateFileLoading,
    isError: isCertificateFileError,
  } = useGetFileByIdQuery(isNotNil(certificateFileId) ? {fileId: certificateFileId} : skipToken);

  // The note endpoint 404s when a vehicle has none, which is the normal path. Any other failure is
  // real: treating it as "no note" would open the field blank and overwrite the note on save.
  const {
    data: additionalNote,
    isLoading: isAdditionalNoteLoading,
    error: additionalNoteError,
  } = useGetAdditionalNoteQuery(
    {vehicleId: props.vehicleId},
    {skip: isNilOrEmpty(props.vehicleId)}
  );
  const isAdditionalNoteError =
    isNotNil(additionalNoteError) && !is404ApiError(additionalNoteError);

  const {
    serviceVehicleId,
    vehicleClassId,
    isLoading: isServiceVehicleLoading,
  } = useGetServiceVehicleV2Query(
    {vehicleId: props.vehicleId},
    {
      skip: isNilOrEmpty(props.vehicleId),
      selectFromResult: ({data, isLoading}) => ({
        serviceVehicleId: data?.serviceVehicleId,
        vehicleClassId: data?.vehicleClassId,
        isLoading,
      }),
    }
  );

  const isLoading =
    isVehicleLoading ||
    isCertificateFileLoading ||
    isAdditionalNoteLoading ||
    isServiceVehicleLoading;
  // A failed certificate lookup would seed the form with no document and null it on save, and a
  // note lookup that fails with anything but 404 would hide a note the save then overwrites.
  // The service-vehicle lookup is deliberately absent: it 404s for a vehicle that simply has no
  // service record, and shouldShowVehicleClass already withholds the one field it governs.
  const isError = isVehicleError || isCertificateFileError || isAdditionalNoteError;

  // DataStatus replaces its children, so these states need their own shell - bare, they would
  // drop a loose spinner into the host's layout and show no modal at all.
  if (isLoading || isError || isNil(vehicle)) {
    return (
      <FullScreenModal
        headline={i18n.t('entity.vehicle.labels.editServiceVehicle')}
        onClose={props.onDiscard}
      >
        <DataStatus
          isLoading={isLoading}
          isError={isError}
          isEmpty={!isLoading && isNil(vehicle)}
          emptyMessage={i18n.t('page.vehicle.notifications.notFound')}
          minHeight={100}
        />
      </FullScreenModal>
    );
  }

  return (
    <EditServiceVehicleForm
      {...props}
      vehicle={vehicle}
      certificateFile={certificateFile}
      additionalNote={additionalNote?.note}
      serviceVehicleId={serviceVehicleId}
      vehicleClassId={vehicleClassId}
    />
  );
}
